/**
 * Server-rendered "documents that mention this glossary term", as real
 * `<a href>`.
 *
 * The data is `content.auto_referencing_documents`, derived by phrase-querying
 * the extracted PDF text in Solr (backend
 * `scripts/glossary_backlinks_reconcile.py`). It shipped first as a pane inside
 * DocumentTabs, which hydrates client-side only — so the one audience the panel
 * exists for, a crawler fetching the term's page, saw none of it. This is the
 * same list, rendered flat and unconditionally alongside VersionRelations, for
 * the same reason and by the same mechanism: the links have to be in the server
 * response.
 *
 * The caption stays. These are search hits, not relations an editor asserted,
 * and moving them next to the curated block must not let them borrow that
 * authority — the heading says auto-detected and the note says how they were
 * found, exactly as the pane did.
 *
 * Renders nothing when the field is absent or empty, so this is safe on every
 * term that has not been through the reconciler.
 *
 * Rendered as a native `<details>`, collapsed by default (BL-0020, same
 * pattern as VersionRelations/BL-0015): up to 20 links (`MAX_BACKLINKS`) is
 * short enough that this was not the worst offender, but there is no reason
 * for it to be the one `pp-relations` block left always-open once its
 * sibling was collapsed for exactly this reason. `<details>` content is
 * present in the DOM either way, so a crawler sees no difference.
 */
import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

const AutoReferences = ({ content }) => {
  const items = Array.isArray(content?.auto_referencing_documents)
    ? content.auto_referencing_documents
    : [];
  if (items.length === 0) return null;

  return (
    <details
      className="pp-relations pp-relations--auto"
      aria-label="Documents that mention this term"
    >
      <summary className="pp-relations__summary">
        Mentioned in (auto-detected)
      </summary>
      <div className="pp-relations__body">
        <div className="pp-relations__group">
          <p className="pp-relations__hint">
            Automatically detected by searching the text of documents in this
            archive for this term. Not reviewed by an editor.
          </p>
          <ul className="pp-relations__list">
            {items.map((item, i) => {
              // docid is NOT unique in this corpus (see the reconciler); the
              // uid or the url is what identifies an entry.
              const href = item.url ? flattenToAppURL(item.url) : null;
              return (
                <li
                  className="pp-relations__item"
                  key={item.uid || item.url || i}
                >
                  {href ? (
                    <UniversalLink href={href} className="pp-relations__link">
                      {item.docid ? (
                        <span className="pp-relations__docid">
                          {item.docid}
                        </span>
                      ) : null}
                      <span className="pp-relations__title">
                        {item.title}
                      </span>
                    </UniversalLink>
                  ) : (
                    <span className="pp-relations__link">
                      {item.docid ? (
                        <span className="pp-relations__docid">
                          {item.docid}
                        </span>
                      ) : null}
                      <span className="pp-relations__title">
                        {item.title}
                      </span>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </details>
  );
};

export default AutoReferences;
